import { ChevronDown, ChevronRight } from 'lucide-react';

import { useEffect, useState } from 'react';

import { formatCurrency } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';

import { SplitFiestaLoading } from '@/components/common/SplitFiestaLoading';

import { useGroupCategoryReportQuery } from '@/hooks/queries/useReports';

interface CategoryBreakdownProps {
  groupId: string;
  startMonth: string;
  endMonth: string;
  currency: string;
  onSelectCategory?: (category: string) => void;
}

const CATEGORY_COLORS = [
  'bg-blue-500',
  'bg-green-500',
  'bg-yellow-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-indigo-500',
  'bg-red-500',
  'bg-orange-500',
  'bg-teal-600',
  'bg-fuchsia-600',
];

function CategoryRow({
  colorClass,
  name,
  detail,
  detailId,
  amount,
  percent,
  hintTestId,
  onOpen,
  openLabel,
}: {
  colorClass: string;
  name: string;
  detail: string;
  detailId: string;
  amount: string;
  percent: string;
  hintTestId: string;
  onOpen?: () => void;
  openLabel: string;
}) {
  const body = (
    <>
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-4 h-4 shrink-0 rounded-full ${colorClass}`} />
        <div className="min-w-0 text-left">
          <p className="font-medium text-gray-900 dark:text-white">{name}</p>
          <p
            id={detailId}
            className="text-xs sm:text-sm text-gray-500 dark:text-gray-400"
          >
            {detail}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div className="text-right">
          <p className="font-semibold text-gray-900 dark:text-white text-xs sm:text-sm lg:text-base">
            {amount}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{percent}</p>
        </div>
        {onOpen ? (
          <ChevronRight
            aria-hidden="true"
            data-testid={hintTestId}
            className="w-4 h-4 text-gray-400"
          />
        ) : null}
      </div>
    </>
  );
  const rowClass = 'flex flex-1 items-center justify-between gap-3 min-w-0';
  if (!onOpen) return <div className={rowClass}>{body}</div>;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={openLabel}
      // The label replaces the row's text, so the counts still need announcing.
      aria-describedby={detailId}
      className={`${rowClass} rounded-lg -mx-2 px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-700/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500`}
    >
      {body}
    </button>
  );
}

export default function CategoryBreakdown({
  groupId,
  startMonth,
  endMonth,
  currency,
  onSelectCategory,
}: CategoryBreakdownProps) {
  const { t } = useTranslation();
  useNamespace('reports');
  useNamespace('expenses');
  const { data: categories, isLoading } = useGroupCategoryReportQuery(
    groupId,
    startMonth,
    endMonth
  );

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );

  // Auto-expand categories with subcategories when data loads
  // Use functional update to avoid setting state directly in response to prop changes
  useEffect(() => {
    if (categories) {
      const categoriesWithSubcategories = categories
        .filter(cat => cat.subcategories && cat.subcategories.length > 0)
        .map(cat => cat.category);

      // Wrap setState in setTimeout to avoid direct state setting in effect
      const timer = setTimeout(() => {
        setExpandedCategories(prev => {
          const newSet = new Set(categoriesWithSubcategories);
          // Only update if the set actually changed
          if (
            prev.size !== newSet.size ||
            ![...prev].every(cat => newSet.has(cat))
          ) {
            return newSet;
          }
          return prev;
        });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [categories]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <SplitFiestaLoading className="bg-transparent" />
      </div>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-gray-400">
        {t('reports:noData')}
      </div>
    );
  }

  const total = categories.reduce((sum, cat) => sum + cat.totalAmount, 0);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center">
        {t('reports:category.title')}
      </h2>

      {/* Category List */}
      <div className="space-y-4">
        {categories.map((category, index) => {
          const percentage =
            total > 0 ? (category.totalAmount / total) * 100 : 0;

          const hasSubcategories =
            category.subcategories && category.subcategories.length > 0;
          const isExpanded = expandedCategories.has(category.category);

          return (
            <div key={category.category} className="space-y-2">
              <div className="flex items-center gap-2">
                {hasSubcategories ? (
                  <button
                    type="button"
                    data-testid={`category-toggle-${category.category}`}
                    aria-expanded={isExpanded}
                    aria-label={t('reports:drilldown.toggleSubcategories', {
                      name: t(`expenses:categories.${category.category}`),
                    })}
                    onClick={() => toggleCategory(category.category)}
                    className="shrink-0 rounded p-0.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </button>
                ) : null}
                <CategoryRow
                  colorClass={CATEGORY_COLORS[index % CATEGORY_COLORS.length]}
                  name={t(`expenses:categories.${category.category}`)}
                  detail={`${category.expenseCount} ${t('reports:category.expenses')} • ${t('reports:category.average')} ${formatCurrency(category.averagePerExpense, currency)}`}
                  detailId={`category-detail-${category.category}`}
                  amount={formatCurrency(category.totalAmount, currency)}
                  percent={
                    percentage > 0 && percentage < 0.1
                      ? '<0.1%'
                      : `${percentage.toFixed(1)}%`
                  }
                  hintTestId={`category-open-hint-${category.category}`}
                  onOpen={
                    onSelectCategory
                      ? () => onSelectCategory(category.category)
                      : undefined
                  }
                  openLabel={t('reports:drilldown.openCategory', {
                    name: t(`expenses:categories.${category.category}`),
                  })}
                />
              </div>

              {/* Progress Bar */}
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${
                    CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                  } transition-all duration-500`}
                  style={{ width: `${percentage}%` }}
                />
              </div>

              {/* Subcategories */}
              {hasSubcategories && isExpanded && category.subcategories && (
                <div className="ml-8 space-y-2 mt-3">
                  {category.subcategories.map(subcategory => (
                    <div
                      key={subcategory.subcategory}
                      className="flex items-center justify-between py-1"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-2 h-2 shrink-0 rounded-full ${
                            CATEGORY_COLORS[index % CATEGORY_COLORS.length]
                          } opacity-60`}
                        />
                        <div>
                          <p className="text-xs text-gray-700 dark:text-gray-300">
                            {/* When subcategory equals category, display the category translation */}
                            {subcategory.subcategory === category.category
                              ? t(`expenses:categories.${category.category}`)
                              : subcategory.subcategory !== 'default' &&
                                  t(
                                    `expenses:subcategories.${category.category}.${subcategory.subcategory}`
                                  ) !==
                                    `expenses:subcategories.${category.category}.${subcategory.subcategory}`
                                ? t(
                                    `expenses:subcategories.${category.category}.${subcategory.subcategory}`
                                  )
                                : subcategory.subcategory
                                    .charAt(0)
                                    .toUpperCase() +
                                  subcategory.subcategory.slice(1)}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {subcategory.expenseCount}{' '}
                            {t('reports:category.expenses')} •{' '}
                            {t('reports:category.average')}{' '}
                            {formatCurrency(
                              subcategory.averagePerExpense,
                              currency
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {formatCurrency(subcategory.totalAmount, currency)}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {subcategory.percentageOfCategory.toFixed(1)}% of{' '}
                          {t(
                            `expenses:categories.${category.category}`
                          ).toLowerCase()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <p className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('reports:category.total')}
          </p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">
            {formatCurrency(total, currency)}
          </p>
        </div>
      </div>
    </div>
  );
}
